import { useEffect, useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { useLang } from '../../../Hooks'; 
import {
  getProjectFolders,
  getFolderContents,
  createFolder,
  updateFolder,
  deleteFolder,
} from "../../../Services/api";

export default function useFileManager({ ContractData, Tab }) {
  const toast = useToast();
  const { texts } = useLang(); 
  
  const notify = useCallback((status, message) => {
    toast({
      description: message,
      status: status,
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  }, [toast]);

  const [folders, setFolders] = useState([]);
  const [folderData, setFolderData] = useState({ Folders: [], Files: [] });
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [modalState, setModalState] = useState({ 
    IsOpen: false, 
    Tab: "", 
    ParentId: null, 
    FolderId: null, 
    FileName: null, 
    File: null 
  });
  const [isFoldersLoading, setIsFoldersLoading] = useState(false);
  const [isFolderDataLoading, setIsFolderDataLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFolders, setOpenFolders] = useState({});

  const ContractId = ContractData?.ContractId;

  const fileManagerTexts = texts?.fileManager || {};
  const generalTexts = texts?.general || {};

  const fetchFolders = useCallback(async () => {
    if (!ContractId) {
      setFolders([]);
      return;
    }
    
    setIsFoldersLoading(true);
    try {
      const { data } = await getProjectFolders(ContractId, 'Reports');
      const fetchedFolders = data?.data?.results || [];
      setFolders(fetchedFolders);
      
      if (fetchedFolders.length > 0 && selectedFolder === null) {
        setSelectedFolder(fetchedFolders[0].FolderId);
      }
    } catch (error) {
      notify("error", fileManagerTexts.failedToLoadFolders || "Failed to load folders.");
    } finally {
      setIsFoldersLoading(false);
    }
  }, [ContractId, fileManagerTexts, notify, selectedFolder]);

  const fetchFolderContents = useCallback(async (folderId = selectedFolder) => {
    if (!ContractId) {
      setFolderData({ Folders: [], Files: [] });
      return;
    }
    
    setIsFolderDataLoading(true);
    try {

      let folderIdToSend;
      
      if (folderId) {

        folderIdToSend = folderId;
      } else if (folders.length > 0) {

        folderIdToSend = folders[0].FolderId;
      } else {

        folderIdToSend = 0;
      }
      
      const { data } = await getFolderContents(ContractId, folderIdToSend);
      setFolderData(data?.data || { Folders: [], Files: [] });
    } catch (error) {
      notify("error", fileManagerTexts.failedToLoadFolderContents || "Failed to load folder contents.");
    } finally {
      setIsFolderDataLoading(false);
    }
  }, [ContractId, selectedFolder, folders, fileManagerTexts, notify]);


  useEffect(() => {
    fetchFolders();
    setOpenFolders({});
  }, [ContractId]); 


  useEffect(() => {
    if (selectedFolder !== null || folders.length > 0 || ContractId) {
      fetchFolderContents();
    }
  }, [selectedFolder, ContractId]); 

  const handleSubmit = async (form) => {
    setIsSubmitting(true);
    let success = false;
    try {
      let response;
      if (form.Tab === "Create") {
        response = await createFolder({ 
          FolderName: form.FolderName, 
          ContractId, 
          ParentId: form.ParentId 
        });
      } else if (form.Tab === "Edit") {
        response = await updateFolder(form.FolderId, { 
          FolderName: form.FolderName 
        });
      } else if (form.Tab === "Delete") {
        response = await deleteFolder(form.FolderId);
      }
      
      if (response && response.data && !response.data.error) {
        notify("success", response.data.message || generalTexts.success || "Success!");
        success = true;
      } else {
        throw new Error(response?.data?.message || generalTexts.unknownError || "An unknown error occurred.");
      }
      
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      let translatedError = errorMessage;
      
      if (errorMessage.includes("Folder name is required")) {
        translatedError = fileManagerTexts.folderNameRequired || "Folder name is required";
      } else if (errorMessage.includes("already exists")) {
        translatedError = fileManagerTexts.folderAlreadyExists || "Folder with this name already exists";
      } else if (errorMessage.includes("not found")) {
        translatedError = fileManagerTexts.folderNotFound || "Folder not found";
      } else if (errorMessage.includes("permission")) {
        translatedError = fileManagerTexts.permissionDenied || "Permission denied";
      }
      
      notify("error", translatedError);
      success = false;
    } finally {
      if (success) {
        if (form.Tab === 'Create' || form.Tab === 'Delete') {
          await fetchFolders();
        }
        await fetchFolderContents();
        if (form.Tab === "Delete" && selectedFolder === form.FolderId) {
          setSelectedFolder(null);
        }
      }
      setIsSubmitting(false);
    }
    
    return success;
  };
  
  const handleChange = (newState) => {
    if (newState === "Reset") {
      setModalState({ IsOpen: false });
      return;
    }
    setModalState(prev => ({...prev, ...newState}));
  };

  const onSelectFolder = useCallback((item) => {
    const newSelectedFolder = item ? item.FolderId : null;
    setSelectedFolder(newSelectedFolder);
    
    if (ContractId) {
      setIsFolderDataLoading(true);
      getFolderContents(ContractId, newSelectedFolder || folders[0]?.FolderId || 0)
        .then(({ data }) => {
          setFolderData(data?.data || { Folders: [], Files: [] });
        })
        .catch(() => {
          notify("error", fileManagerTexts.failedToLoadFolderContents || "Failed to load folder contents.");
        })
        .finally(() => {
          setIsFolderDataLoading(false);
        });
    }
  }, [ContractId, folders, notify, fileManagerTexts]);

  const onToggleFolder = (folderId) => {
    setOpenFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const loadingMessages = {
    create: fileManagerTexts.creatingFolder || "Creating folder...",
    edit: fileManagerTexts.updatingFolder || "Updating folder...",
    delete: fileManagerTexts.deletingFolder || "Deleting folder..."
  };

  return {
    Folders: folders,
    FolderData: folderData,
    OnRefetchFolders: fetchFolders,
    OnRefetchFolder: fetchFolderContents,
    IsFoldersLoading: isFoldersLoading,
    IsFolderContentsLoading: isFolderDataLoading,
    IsAddFolderLoading: isSubmitting,
    IsUpdateFolderLoading: isSubmitting,
    IsDeleteFolderLoading: isSubmitting,
    State: modalState,
    SelectedFolder: selectedFolder,
    OpenFolders: openFolders,
    OnToggleFolder: onToggleFolder,
    OnSubmit: handleSubmit,
    OnChange: handleChange,
    OnSelectFolder: onSelectFolder,
    LoadingMessages: loadingMessages,
    Texts: texts 
  };
}